import { useState } from 'react';
import { api, ApiError, type Restaurant } from './api';

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 14,
  borderRadius: 6,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  color: '#fff',
} as const;

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 4,
} as const;

/**
 * Point a restaurant's POS at a Scandeer service location.
 *
 * Two things this panel must get right, both of them about secrets:
 *
 *   * The API key and webhook secret are never sent to this browser — the
 *     server redacts them to `hasApiKey` / `hasWebhookSecret`. So the two
 *     fields start blank even for a connected restaurant, and leaving them
 *     blank on a re-save keeps what is stored. The copy says so, because a
 *     blank field that silently means "unchanged" is otherwise alarming.
 *
 *   * Connecting Scandeer switches delivery AWAY from the POS's own WhatsApp
 *     ordering, and the two never run together. The confirmation is explicit
 *     rather than a side effect of saving a form.
 *
 * See docs/SCANDEER-DELIVERY.md §4 and §5.
 */
export function ScandeerConnectPanel({
  restaurant,
  onConnected,
}: {
  restaurant: Restaurant;
  onConnected: () => Promise<void>;
}) {
  const cfg = restaurant.scandeer_config;
  // Built exactly as the server builds it in connectScandeer, so what an
  // operator pastes by hand and what Connect registers cannot drift apart.
  const posWebhookUrl = restaurant.base_url
    ? `${restaurant.base_url.replace(/\/+$/, '')}/api/webhook/scandeer`
    : '';
  const connected = restaurant.delivery_provider === 'scandeer' && !!cfg?.locationId;

  const [open, setOpen] = useState(!connected);
  const [baseUrl, setBaseUrl] = useState(cfg?.baseUrl || 'https://hub.scandeer.com');
  // Filled in from the key where Scandeer can be reached from here, typed by
  // hand where it cannot. Either way the till ends up with the same two ids.
  const [serviceId, setServiceId] = useState(cfg?.serviceId || '');
  const [locationId, setLocationId] = useState(cfg?.locationId || '');
  const [showIds, setShowIds] = useState(false);
  const [copied, setCopied] = useState(false);
  // Never prefilled; see the note above.
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const keyOnFile = !!cfg?.hasApiKey;
  const secretOnFile = !!cfg?.hasWebhookSecret;

  if (!restaurant.has_delivery) {
    return (
      <section className="card connect" style={{ marginTop: 24 }}>
        <div className="pane-head">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🛵</span> Scandeer Delivery
          </h2>
        </div>
        <p className="hint" style={{ margin: 0 }}>
          The Delivery module is not in this restaurant's plan. Enable it above before connecting a
          Scandeer location.
        </p>
      </section>
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const res = await api.connectScandeer(restaurant.id, {
        baseUrl: baseUrl.trim(),
        serviceId: serviceId.trim() || undefined,
        locationId: locationId.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined,
        deliveryProvider: 'scandeer',
      });
      setMsg(res.message);
      setApiKey('');
      setWebhookSecret('');
      await onConnected();
      setOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save the Scandeer connection');
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (
      !confirm(
        'Disconnect Scandeer delivery?\n\nThe stored API key is deleted and delivery goes back to this POS’s own WhatsApp ordering. Orders already in the kitchen are unaffected.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const res = await api.connectScandeer(restaurant.id, { deliveryProvider: 'pos_whatsapp' });
      setMsg(res.message);
      await onConnected();
      setOpen(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not disconnect Scandeer delivery');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card connect" style={{ marginTop: 24 }}>
      <div className="pane-head">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🛵</span> Scandeer Delivery
        </h2>
        <span className="spacer" />
        {connected && (
          <button className="btn sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : '⚙️ Manage'}
          </button>
        )}
      </div>

      {msg && <div className="banner ok">{msg}</div>}
      {err && <div className="banner err">{err}</div>}

      {connected && !open && (
        <div className="banner ok" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <strong>Scandeer delivery is connected.</strong> Location:{' '}
            <span className="mono">{cfg?.locationId}</span> · Service:{' '}
            <span className="mono">{cfg?.serviceId}</span>
          </div>
        </div>
      )}

      {open && (
        <>
          <p className="hint" style={{ fontSize: 13, lineHeight: 1.6 }}>
            Paste the API key from the Scandeer dashboard. Everything else — which service, which
            location, and where Scandeer should deliver orders — is read from the key itself and
            set up for you.
          </p>
          <p className="hint" style={{ fontSize: 13, lineHeight: 1.6 }}>
            A restaurant takes delivery orders from <strong>one</strong> place. Connecting Scandeer
            makes it that place. Dine-in, takeaway and orders typed at the till are unaffected.
          </p>
          {!restaurant.base_url && (
            <div className="banner err">
              This restaurant has not been deployed yet, so there is no address to send orders to.
              Deploy it first.
            </div>
          )}

          {/*
            Shown always, not only on failure.

            Connecting registers this address with Scandeer automatically. When
            that call cannot be made — the hub unreachable from here, a key
            edited in the Scandeer dashboard instead — the fallback is to paste
            it onto the key by hand, and until now this screen said to do that
            without ever showing what to paste. The address is derived from the
            restaurant's own base URL, so it is known whether or not Scandeer
            answered.
          */}
          {restaurant.base_url && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>POS webhook URL</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <input readOnly value={posWebhookUrl} style={{ ...inputStyle, flex: 1 }} />
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    void navigator.clipboard
                      ?.writeText(posWebhookUrl)
                      .then(() => setCopied(true))
                      .catch(() => setCopied(false));
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="hint" style={{ fontSize: 12, marginTop: 4 }}>
                Where Scandeer delivers this restaurant's orders. Connecting sets it for you — paste
                it into <strong>POS webhook URL</strong> on the key in the Scandeer dashboard only if
                this panel reports that it could not.
              </p>
            </div>
          )}

          <form onSubmit={save}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Hub URL</label>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://hub.scandeer.com"
                style={inputStyle}
              />
              <p className="hint" style={{ fontSize: 12, marginTop: 4 }}>
                Must be https — the API key travels on every call to it.
              </p>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                API key {keyOnFile && <span className="mono">· one is on file</span>}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="new-password"
                placeholder={keyOnFile ? 'Leave blank to keep the stored key' : 'sk_live_…'}
                style={inputStyle}
              />
              <p className="hint" style={{ fontSize: 12, marginTop: 4 }}>
                Generated in the Scandeer service dashboard, restricted to exactly one location,
                with <strong>Catalog Write</strong> and <strong>Orders Status</strong> ticked. It is
                never sent back to this screen, so leaving it blank keeps what is stored.
              </p>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Webhook secret <span style={{ fontWeight: 400, opacity: 0.7 }}>· optional</span>
                {secretOnFile && <span className="mono"> · one is on file</span>}
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                autoComplete="new-password"
                placeholder={
                  secretOnFile ? 'Leave blank to keep the stored secret' : 'Leave blank unless you set one in Scandeer'
                }
                style={inputStyle}
              />
              <p className="hint" style={{ fontSize: 12, marginTop: 4 }}>
                Only needed if you set a <strong>POS webhook secret</strong> on the Scandeer key.
                Left blank, inbound orders are verified against the API key above instead — which
                is one credential to manage rather than two. Either way, every order is verified.
              </p>
            </div>

            <div style={{ marginBottom: 14 }}>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => setShowIds((v) => !v)}
                style={{ fontSize: 12, padding: '2px 0', opacity: 0.8 }}
              >
                {showIds ? '▲ Hide' : '▼'} Service and Location ID
                {cfg?.locationId ? ` · ${cfg.locationId}` : ''}
              </button>
              {showIds && (
                <div style={{ marginTop: 10 }}>
                  <p className="hint" style={{ fontSize: 12, marginTop: 0, marginBottom: 10 }}>
                    Normally read from the key and left alone. Fill these in only if saving reports
                    that Scandeer could not be reached from here &mdash; the till talks to Scandeer
                    itself and may have a route this portal does not.
                  </p>
                  <label style={labelStyle}>Service ID</label>
                  <input
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value)}
                    placeholder="Read from the key when possible"
                    style={{ ...inputStyle, marginBottom: 10 }}
                  />
                  <label style={labelStyle}>Location ID</label>
                  <input
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    placeholder="The one Scandeer location this POS serves"
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            <div className="actions" style={{ marginTop: 8, display: 'flex', gap: 10 }}>
              <button
                className="btn primary"
                disabled={busy || !apiKey.trim() && !keyOnFile}
                style={{ padding: '10px 24px', fontSize: 14 }}
              >
                {busy ? 'Saving & Syncing…' : '💾 Save & Sync Scandeer Delivery'}
              </button>
              {connected && (
                <button type="button" className="btn ghost" disabled={busy} onClick={disconnect}>
                  Disconnect
                </button>
              )}
            </div>
          </form>
        </>
      )}
    </section>
  );
}
